import { TemplateResult, LitElement, html } from 'lit';
import { customElement, property, queryAssignedElements } from 'lit/decorators.js';
import globalStyles from './styles/outline-hero.global.pcss?inline';
import encapsulatedStyles from './styles/outline-hero.encapsulated.pcss?inline';
import { AdoptedStyleSheets } from '../../controllers/adopted-stylesheets';
import { screenSizeMap } from '../../controllers/screen-size-controller';
import { validateSlot } from '../../utilities/slot';

import '../outline-container/outline-container';

export const heroImagelessGradients = [
  'gradient-blue', 'gradient-gold',       'gradient-blue',  'gradient-gold',
  'gradient-teal', 'gradient-dark-gold',  'gradient-green', 'gradient-purple',
  'none',] as const;
export type HeroImagelessGradient = (typeof heroImagelessGradients)[number];

/**
 * The element name, reused throughout the codebase
 * @type {string}
 */
const componentName = 'outline-hero';
/**
 * The Outline Hero component
 *
 * A reusable LitElement component for heros.
 *
 * @element outline-hero
 * @extends LitElement
 * @slot breadcrumb - For ouline-breadcrumb.
 * @slot eyebrow - For ouline-header with a span.
 * @slot header - For ouline-header with an h1.
 * @slot description - For ouline-ckeditor content.
 * @slot cta - For ouline-button thin-square variants.
 * @slot image - For ouline-image.
 */
@customElement(componentName)
export class OutlineHero extends LitElement {
  resizeObserver: ResizeObserver;

  /**
   * Adopted stylesheets for the component
   *
   * @type {AdoptedStyleSheets}
   */
  adoptedStyleSheets = new AdoptedStyleSheets(this, {
    globalCSS: globalStyles,
    encapsulatedCSS: encapsulatedStyles,
  });

  @queryAssignedElements({ slot: 'breadcrumb' })
  breadcrumbRef: Array<HTMLElement>;

  @queryAssignedElements({ slot: 'eyebrow' })
  eyebrowRef: Array<HTMLElement>;

  @queryAssignedElements({ slot: 'heading' })
  headingRef: Array<HTMLElement>;

  @queryAssignedElements({ slot: 'description' })
  descriptionRef: Array<HTMLElement>;

  @queryAssignedElements({ slot: 'cta' })
  ctaRef: Array<HTMLElement>;

  @queryAssignedElements({ slot: 'image' })
  imageRef: Array<HTMLElement>;

  @property({ type: Boolean, reflect: true, attribute: 'is-mobile' })
  isMobile: boolean;

  @property({ type: String, reflect: true })
  gradient: HeroImagelessGradient = 'none';

  @property({ type: Boolean, reflect: true, attribute: 'has-image' })
  hasImage: boolean;

  @property({ type: String, reflect: true, attribute: 'cta-class' })
  ctaClass = '';

  @property({ type: String, reflect: true, attribute: 'no-image-class' })
  noImageClass = '';

  @property({ type: String, reflect: true, attribute: 'background-image' })
  backgroundImage: string;

  @property({ type: String, reflect: true, attribute: 'mobile-background-image' })
  mobileBackgroundImage: string;

  _selfRef: HTMLElement;


  connectedCallback() {
    super.connectedCallback();
    this.resizeObserver = new ResizeObserver(() => {
      this.resetAtScreen();
    });
  }

  disconnectedCallback() {
    this.resizeObserver.disconnect();
  }

  firstUpdated() {
    this.setSelfRef();
    this.handleImage();
    validateSlot(this.breadcrumbRef, '.outline-hero--bradcrumb', this.shadowRoot);
    validateSlot(this.imageRef, '.outline-hero--image', this.shadowRoot);
    validateSlot(this.eyebrowRef, '.outline-hero--eyebrow', this.shadowRoot);
    validateSlot(this.headingRef, '.outline-hero--heading', this.shadowRoot);
    validateSlot(this.descriptionRef, '.outline-hero--description', this.shadowRoot);
    validateSlot(this.ctaRef, '.outline-hero--cta', this.shadowRoot);
    this.resizeObserver.observe(this._selfRef);
  }

  render(): TemplateResult {
    return html`
    <section class="outline-hero">
      <div class="outline-hero--breadcrumb">
        <slot name="breadcrumb"></slot>
      </div>
      <outline-container background-img='${this.isMobile ? this.mobileBackgroundImage : this.backgroundImage}' 
        background-color='${this.gradient !== 'none' ? `gradient-${this.gradient}` : `transparent` }'
        container-width="full-bleed">
        <div class="outline-hero--main-wrapper">
          <div class="outline-hero--content ${this.noImageClass}">
            <div class="outline-hero--eyebrow"> 
              <slot name="eyebrow"></slot>
            </div>
            <div class="outline-hero--heading">
              <slot name="heading"></slot>
            </div>
            <div class="outline-hero--description">
              <slot name="description"></slot>
            </div>
            <div class="outline-hero--cta ${this.ctaClass}"> 
              <slot name="cta"></slot>
            </div>
          </div>
          <div class="outline-hero--image">
            <slot name="image"></slot>
          </div>
        </div>
      </outline-container>
    </section>
      `;
  }

  resetAtScreen() {
    this.isMobile = window.innerWidth < (screenSizeMap.get('lg') as number);
  }

  setSelfRef() {
    const self = this.shadowRoot?.querySelector('.outline-hero') as HTMLElement;
    if (self) {
      this._selfRef = self;
    }
  }

  handleImage() {
    this.hasImage = !!this.imageRef.length;
    const slottedImageContent = !!this.imageRef[0]?.querySelector('img');
    if (slottedImageContent) {
      const imgSrc = this.imageRef[0].querySelector('img')?.getAttribute('src');
      if (imgSrc || imgSrc === '') {
        const validImage = /\.(jpeg|jpg|png|gif)\b/i.test(imgSrc);
        if (!validImage || imgSrc === '') {
          this.imageRef[0].querySelector('img')?.remove();
          this.setAttribute('image-broken', 'true');
        }
      }
      if (!imgSrc) {
        this.imageRef[0].querySelector('img')?.remove();
        this.setAttribute('image-broken', 'true');
      }
    }
  }
}

/**
 * Declare the component in the global HTMLElementTagNameMap
 */
declare global {
  interface HTMLElementTagNameMap {
    [componentName]: OutlineHero;
  }
}
